import classNames from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import { useFieldArray, useFormContext } from 'react-hook-form'

import { UnitContentCard } from '@apps-common/apps/portal/pages/units/pages/manage/components'
import { ControlEventCheckForm } from '@apps-common/apps/portal/pages/units/pages/manage/pages/controls/pages/modify/components'
import { SectionTitle } from '@apps-common/components/common/portal-title'
import { ButtonRoundSm } from '@apps-common/components/elements/button'
import { InputSM } from '@apps-common/components/elements/form'
import { VertSpacerMd } from '@apps-common/components/layout'
import { CONTROL_TYPE_ALARM } from '@apps-common/config/controls'
import { commonIcons } from '@apps-common/config/icons'
import { FORM_DEFAULT_REQUIRED } from '@apps-common/config/react-hook-form'

export interface IControlCustomStateFormProps {
  defaultOnState: string
  description: string
  code: string
  zone: string
  partition: string
}
interface IProps {
  loading: boolean
  disabled: boolean
}

export const ControlCustomStatesForm: React.FC<IProps> = ({ loading, disabled }) => {
  const { register, control, watch, formState } = useFormContext()
  const controlTypeId = watch('controlTypeId')

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'customStates',
    rules: {
      required: false,
      minLength: 0
    }
  })

  const rootError = formState?.errors?.customStates?.root?.message as string
  const errors = formState?.errors?.customStates as any

  if (controlTypeId !== CONTROL_TYPE_ALARM) {
    return (
      <div>
        <UnitContentCard
          classes={classNames('!px-4 !py-3 relative bg-surface border border-border text-text', rootError && 'border border-error')}
        >
          <div className='pl-[10px] flex items-center justify-between'>
            <SectionTitle text='States' />
            <ButtonRoundSm
              type='button'
              className='action-success'
              loading={loading || disabled}
              callback={() =>
                append({
                  unitControlStateId: 0,
                  unitControlId: 0,
                  stateName: '',
                  alarmPartition: '',
                  userId: 0,
                  dateEdited: new Date(),
                  eventChecks: []
                })
              }
            >
              <commonIcons.add />
            </ButtonRoundSm>
          </div>
        </UnitContentCard>
        <motion.div>
          <AnimatePresence mode='popLayout' initial={false}>
            {fields.map((field: any, index) => (
              <motion.div
                key={`${field.id}.customStates.container`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                <VertSpacerMd />
                <UnitContentCard classes='bg-surface border border-border'>
                  <div className='flex items-center justify-between'>
                    <SectionTitle text={`State #${index + 1}`} classes='ml-2 !text-[0.9em]' />
                    <ButtonRoundSm
                      type='button'
                      className='action-warning'
                      loading={loading || disabled}
                      callback={() => {
                        remove(index)
                      }}
                    >
                      <commonIcons.remove />
                    </ButtonRoundSm>
                  </div>
                  <VertSpacerMd />
                  <div className='flex flex-col gap-[15px]'>
                    <InputSM
                      key={`${field.id}.stateName`}
                      type='text'
                      placeholder='Name to identify this state'
                      label='State name'
                      id={`customStates.${index}.stateName`}
                      showLabel
                      loading={loading}
                      disabled={disabled}
                      registration={register(`customStates.${index}.stateName`, {
                        required: FORM_DEFAULT_REQUIRED
                      })}
                      errors={(errors as any)?.[index]?.stateName}
                    />
                    <ControlEventCheckForm stateIndex={index} disabled={disabled} />
                  </div>
                </UnitContentCard>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
        {rootError && <p className='pl-[10px] text-[0.8em] text-error jost'>{rootError}</p>}
      </div>
    )
  }

  return null
}
